import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ClipboardList, UserRound } from 'lucide-react';
import { AdvancedFilterPanel, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { SelectedFilterChip } from '@/components/common/SelectedFilterChip';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useKeepWindowScrollOnReturn } from '@/hooks/useKeepWindowScrollOnReturn';
import { parseNumberFilter } from '@/utils/filters';
import { instituteSubmissionInfiniteListQueryOptions } from '../api/instituteSubmissions.api';
import { getInstituteSubmissionColumns } from '../components/InstituteSubmissionColumns';
import { InstituteSubmissionListHeader } from '../components/InstituteSubmissionListHeader';
import { InstituteSubmissionSummaryCards } from '../components/InstituteSubmissionSummaryCards';
import { LIST_PAGE_SIZE } from '../constants/instituteSubmission.constants';
import { useInstituteSubmissionFilterFields } from '../hooks/useInstituteSubmissionFilterFields';
import { useInstituteSubmissionFilters } from '../hooks/useInstituteSubmissionFilters';
import { useLastViewedSubmissionStore } from '../store/lastViewedSubmission.store';
import type { InstituteSubmissionListItem } from '../types/instituteSubmission.types';

const OFFICER_NAME_FALLBACK = 'Selected ZM';

export function InstituteSubmissionListPage() {
  const navigate = useNavigate();
  const {
    searchText,
    filterValues,
    filterOpen,
    setFilterOpen,
    activeFilterCount,
    hasPendingChanges,
    listParams,
    summaryParams,
    countsBySubmitterParams,
    handleSearchApply,
    handleFilterChange,
    applyFilterValues,
  } = useInstituteSubmissionFilters();

  const { fields, optionsFailed } = useInstituteSubmissionFilterFields({
    enabled: filterOpen,
    amUserId: parseNumberFilter(filterValues.amUserId),
    districtId: parseNumberFilter(filterValues.districtId),
    countsBySubmitterParams,
  });

  const {
    rows: submissions,
    isLoading,
    loadError,
    refetch,
    loadMoreProps,
  } = useInfiniteTableQuery(instituteSubmissionInfiniteListQueryOptions(listParams));

  const columns = useMemo(
    () => getInstituteSubmissionColumns({ searchText: listParams.search }),
    [listParams.search],
  );
  const lastViewedSubmissionId = useLastViewedSubmissionStore(
    (state) => state.lastViewedSubmissionId,
  );
  const selectedOfficerName =
    submissions.find((submission) => submission.userId === summaryParams.zmUserId)?.userName ??
    OFFICER_NAME_FALLBACK;

  useKeepWindowScrollOnReturn(lastViewedSubmissionId !== null);

  function handleRowClick(submission: InstituteSubmissionListItem) {
    void navigate({
      to: '/settings/institute-submissions/$submissionId',
      params: { submissionId: submission.id },
    });
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <InstituteSubmissionListHeader
        appliedSearchText={searchText}
        onSearchApply={handleSearchApply}
        filterOpen={filterOpen}
        onFilterToggle={() => setFilterOpen((previous) => !previous)}
        activeFilterCount={activeFilterCount}
      />

      <FilterPanelCollapse isOpen={filterOpen}>
        <div className="flex flex-col gap-2">
          <AdvancedFilterPanel
            fields={fields}
            values={filterValues}
            onChange={handleFilterChange}
            onApply={applyFilterValues}
            hasPendingChanges={hasPendingChanges}
          />
          {optionsFailed && (
            <p className="text-xs text-destructive">
              Some filter options could not be loaded. Refresh the page to try again.
            </p>
          )}
        </div>
      </FilterPanelCollapse>

      {summaryParams.zmUserId !== undefined && (
        <SelectedFilterChip
          icon={UserRound}
          label="Submitted by"
          value={selectedOfficerName}
          clearLabel="Show submissions from everyone"
          onClear={() => applyFilterValues({ ...filterValues, zmUserId: '' })}
        />
      )}

      <InstituteSubmissionSummaryCards params={summaryParams} />

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load institute submissions"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={submissions}
          columns={columns}
          isLoading={isLoading}
          stickyColumnId="instituteName"
          className="lg:flex-1"
          emptyIcon={ClipboardList}
          skeletonRowCount={LIST_PAGE_SIZE}
          {...loadMoreProps}
          isRowHighlighted={(submission) => submission.id === lastViewedSubmissionId}
          onRowClick={handleRowClick}
        />
      )}
    </div>
  );
}
